/**
 * `<script data-search-key="…">` ile **tek etiketlik** kurulum.
 *
 * Amaç: mağazanın sayfasına ikinci bir inline script bloğu koymak zorunda
 * kalmaması. `data-search-key` varsa widget kendini kurar; yoksa hiçbir şey
 * yapmaz ve `SendheapSearch.init({...})` ile elle kurulum yolu aynen çalışır.
 * Eski kurulumlardaki `data-tenant` aynı anahtar olduğu için yedek olarak okunur.
 *
 * ```html
 * <script src=".../widget.js"
 *         data-search-key="…"
 *         data-trigger="#site-search"
 *         data-currency="TRY" defer></script>
 * ```
 *
 * Neden ayrı dosya: `index.ts` sadece API yüzeyini tanımlasın, attribute
 * ayrıştırma/DOM bekleme gibi tarayıcı ayrıntıları burada toplansın.
 */

import type { IInitOptions } from "./types";

/**
 * `document.currentScript` yalnızca script **çalışırken** doludur; sonradan
 * (DOMContentLoaded callback'i içinde) okunursa `null` döner. Bu yüzden modül
 * yüklenirken hemen yakalıyoruz.
 *
 * Not: `type="module"` ile yüklenirse `currentScript` null olur — bizim çıktı
 * klasik IIFE olduğu için normal kullanımda sorun değil.
 */
const hostScript: HTMLScriptElement | null =
  typeof document !== "undefined" &&
  document.currentScript instanceof HTMLScriptElement
    ? document.currentScript
    : null;

/** `"false"` / `"0"` / `"no"` dışındaki her değer (boş string dahil) true. */
const parseBool = (value: string | undefined): boolean | undefined => {
  if (value === undefined) return undefined;
  const normalized = value.trim().toLowerCase();
  if (normalized === "false" || normalized === "0" || normalized === "no")
    return false;
  return true;
};

const parseNum = (value: string | undefined): number | undefined => {
  if (value === undefined || value.trim() === "") return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

/** `undefined` alanları atar — böylece `init` kendi varsayılanlarını uygular. */
const compact = <T extends object>(input: T): T => {
  const out = {} as T;
  (Object.keys(input) as Array<keyof T>).forEach((key) => {
    if (input[key] !== undefined) out[key] = input[key];
  });
  return out;
};

/** `data-*` attribute'larını `init` seçeneklerine çevirir. */
export const optionsFromDataset = (
  script: HTMLScriptElement,
): IInitOptions | null => {
  const d = script.dataset;
  const searchKey = d.searchKey || d.tenant;
  if (!searchKey) return null;

  const theme =
    d.theme === "auto" || d.theme === "light" || d.theme === "dark"
      ? d.theme
      : undefined;

  return compact({
    searchKey,
    email: d.email,
    baseUrl: d.baseUrl,
    trigger: d.trigger,
    mount: d.mount,
    productUrl: d.productUrl,
    searchUrl: d.searchUrl,
    currency: d.currency,
    locale: d.locale,
    theme,
    limit: parseNum(d.limit),
    minChars: parseNum(d.minChars),
    debounce: parseNum(d.debounce),
    cacheSize: parseNum(d.cacheSize),
    hotkey: parseBool(d.hotkey),
    styles: parseBool(d.styles),
    // Metinlerin tamamı attribute'a sığmaz; en sık değiştirileni destekliyoruz.
    // Geri kalanı için `init({ texts: {...} })` kullanılır.
    texts: d.placeholder ? { placeholder: d.placeholder } : undefined,
  }) as IInitOptions;
};

/** DOM hazır olunca çalıştırır — `trigger` seçicisinin elemanı bulunabilsin. */
const whenReady = (run: () => void) => {
  if (typeof document === "undefined") return;
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", run, { once: true });
  } else {
    run();
  }
};

/**
 * Script etiketinde `data-search-key` (ya da eski `data-tenant`) varsa widget'ı kurar ve örneği döndürür.
 * Kurulum asenkron olabildiği için (DOM beklenir) sonuç callback ile verilir.
 */
export const autoInit = (
  init: (options: IInitOptions) => unknown,
  onReady: (instance: unknown) => void,
) => {
  if (!hostScript) return;
  const options = optionsFromDataset(hostScript);
  if (!options) return;

  whenReady(() => {
    try {
      onReady(init(options));
    } catch (error) {
      // Mağazanın sayfasını kırmayalım: hatalı yapılandırma sessizce değil ama
      // yıkıcı da olmayacak şekilde konsola düşsün.
      console.error("[sendheap-search] otomatik kurulum başarısız:", error);
    }
  });
};
